/**
 * Cálculos de precio por canal de Pedidos → Precios de venta. Funciones puras que comparten la sección (dueña de
 * la lógica), la fila de la tabla de desktop y la tarjeta de mobile, para que las tres usen la misma fórmula.
 *
 * El redondeo llega por parámetro (`UtilityService.toFixed`) para no duplicar su ajuste decimal.
 */

/** Decimales del margen o markup que se muestra y se edita. */
export const MARGIN_DECIMAL_PLACES = 2;
/** Decimales del precio de venta por canal; los mismos que guarda la API. */
export const PRICE_DECIMAL_PLACES = 5;
/** Decimales de la ganancia que se muestra bajo el precio. */
export const PROFIT_DECIMAL_PLACES = 2;
/** En margen real el 100% implicaría precio infinito, así que se topa justo por debajo. */
export const MAX_REAL_MARGIN = 99.99;

export type RoundFn = (value: number, decimals: number) => number;

/** Precio y estado de un producto en un canal, tal como lo devuelve el listado. */
export interface ChannelPrice {
    salesChannelId?: string;
    sellingPrice: number | null;
    enabled: boolean;
    name?: string;
}

/** Producto del listado de Precios de venta, con sus canales indexados por id de canal. */
export interface PricedProduct {
    _id: string;
    name?: string;
    cost?: number;
    salesChannels: Record<string, ChannelPrice>;
    [key: string]: any;
}

/**
 * Margen (sobre el precio) o markup (sobre el costo) de un canal. Sin costo o sin precio no hay nada que expresar
 * y vale 0, igual que en la tabla de STG.
 */
export function computeMargin(cost: unknown, sellingPrice: unknown, isMarkup: boolean, round: RoundFn): number {
    const costValue = Number(cost) || 0;
    const price = Number(sellingPrice) || 0;

    if (!costValue || !price) return 0;

    const margin = isMarkup ? ((price - costValue) / costValue) * 100 : (1 - costValue / price) * 100;
    return round(margin, MARGIN_DECIMAL_PLACES);
}

/** Ganancia absoluta del canal: lo que queda sobre el costo. Negativa cuando se vende bajo costo. */
export function computeProfit(cost: unknown, sellingPrice: unknown, round: RoundFn): number {
    const costValue = Number(cost) || 0;
    const price = Number(sellingPrice) || 0;

    if (!costValue || !price) return 0;

    return round(price - costValue, PROFIT_DECIMAL_PLACES);
}

/**
 * Precio de venta que produce un margen o markup dado. Devuelve `null` cuando no se puede derivar: sin costo, con un
 * valor no numérico o si el resultado sería negativo o infinito. En ese caso el precio no se toca.
 *
 * `Number('')` es 0 a propósito, como en STG: vaciar el margen y salir del campo deja el precio en el costo.
 */
export function priceFromMargin(cost: unknown, marginValue: unknown, isMarkup: boolean, round: RoundFn): number | null {
    const costValue = Number(cost) || 0;
    let margin = Number(marginValue);

    if (!Number.isFinite(margin) || !costValue) return null;

    let sellingPrice: number;
    if (isMarkup) {
        sellingPrice = costValue + costValue * (margin / 100);
    } else {
        // En margen real el % tiende a 100 pero nunca lo alcanza (división por <= 0).
        if (margin >= MAX_REAL_MARGIN) margin = MAX_REAL_MARGIN;
        sellingPrice = costValue / (1 - margin / 100);
    }

    if (!Number.isFinite(sellingPrice) || sellingPrice < 0) return null;

    return round(sellingPrice, PRICE_DECIMAL_PLACES);
}

/**
 * Valor de un `<input type="number">` tal como lo entregaría `ngModel`: vacío (o un parcial inválido como "12.",
 * que el navegador reporta vacío) es `null`; si no, el número.
 */
export function parseNumberInput(raw: string | null | undefined): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const value = parseFloat(raw);
    return Number.isNaN(value) ? null : value;
}

/** El canal no cambió: mismo canal, mismo precio y mismo estado. Un lado ausente cuenta como distinto. */
export function isSameChannelState(original: ChannelPrice | undefined, current: ChannelPrice | undefined): boolean {
    if (!original || !current) return original === current;
    return original.salesChannelId === current.salesChannelId && original.sellingPrice === current.sellingPrice && original.enabled === current.enabled;
}

/**
 * Activa o desactiva un producto en un canal con la semántica de STG: al desactivar se restaura el precio original
 * (el que trajo el listado); al activar se conserva el que tenga en pantalla.
 */
export function setChannelValue(product: PricedProduct, original: PricedProduct | undefined, scId: string, value: boolean): void {
    const current = product?.salesChannels?.[scId];
    if (!current) return;

    const originalPrice = original?.salesChannels?.[scId]?.sellingPrice;
    const sellingPrice = value || originalPrice === undefined ? current.sellingPrice : originalPrice;
    product.salesChannels[scId] = { ...current, sellingPrice, enabled: value };
}

/** Estado del checkbox de producto: marcado si vende en todos los canales, indeterminado si en algunos. */
export function productChannelsState(product: PricedProduct, channelIds: string[]): { checked: boolean; indeterminate: boolean } {
    let enabled = 0;
    for (const scId of channelIds) {
        if (product?.salesChannels?.[scId]?.enabled) enabled++;
    }
    const total = channelIds.length;
    return { checked: total > 0 && enabled === total, indeterminate: enabled > 0 && enabled < total };
}

/** Copia profunda de un producto del listado, para guardar el original sin compartir referencias con la pantalla. */
export function cloneProduct<T>(product: T): T {
    return JSON.parse(JSON.stringify(product));
}
